import { updateConfigDoor, useConfigDoor } from '@gozoki/api'
import { OPENING_MODE_VALUES, type OpeningConfigLine } from '@gozoki/api-types'
import { Box, Button } from '@mui/material'
import { useMutation } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { DateTime } from 'luxon'
import { useCallback, useEffect, useState } from 'react'
import { z } from 'zod'
import { AuthorizedButton } from '~/components/communication/AuthorizedButton'
import { useToast } from '~/components/communication/Toast'
import { StoreDetailsLayout } from '~/components/routes/stores/StoreDetailsLayout'
import { OpeningHoursConfigPanel } from '~/components/routes/stores/config/OpeningHoursConfigPanel'
import { STORE_TAB_PERMISSIONS } from '~/components/routes/stores/tabs'
import { withRoutePermissions } from '~/utils/rbac'
import { getApiErrorMessage } from '~/lib/utils'

export const Route = createFileRoute('/_authenticated/stores/$storeId/hours')({
  staticData: {
    permission: STORE_TAB_PERMISSIONS.hours,
  },
  component: StoreOpeningHoursTab,
  params: {
    parse: z.object({ storeId: z.coerce.number() }).parse,
  },
  remountDeps: ({ params }) => params.storeId,
})
withRoutePermissions(Route)

/** Clés en `string` : elles restent acceptées là où l'API attend un `Record<number, …>`. */
type OpeningConfigs = Record<string, OpeningConfigLine[]>

/** Une plage vide sur chacun des sept jours, en attendant la configuration réelle de la porte. */
const emptyOpeningConfigs = (): OpeningConfigs => {
  const midnight = DateTime.now().set({ hour: 0, minute: 0 }).toString()

  return Object.fromEntries(
    [1, 2, 3, 4, 5, 6, 7].map((day) => [
      day,
      [{ beginTime: midnight, endTime: midnight, openingMode: OPENING_MODE_VALUES[0] }],
    ]),
  )
}

function StoreOpeningHoursTab() {
  const { storeId } = Route.useParams()
  const [Toast, showToast] = useToast()

  const [openingConfigs, setOpeningConfigs] = useState<OpeningConfigs>(emptyOpeningConfigs)
  const [isInitializedDoorConfig, setIsInitializedDoorConfig] = useState(false)

  const { data: doorBaseConfiguration } = useConfigDoor(Number(storeId))

  const { mutate: mutateUpdateConfigDoor } = useMutation({
    mutationFn: async () => updateConfigDoor(storeId, openingConfigs),
    onError: (error) => {
      showToast({
        message: getApiErrorMessage(error, 'une erreur est survenue'),
        severity: 'error',
      })
    },
    onSuccess: (response) => {
      showToast({
        message: response.message,
        severity: 'success',
      })
    },
  })

  useEffect(() => {
    if (doorBaseConfiguration && !isInitializedDoorConfig) {
      const convertedConfigs: OpeningConfigs = Object.fromEntries(
        Object.entries(doorBaseConfiguration).map(([day, configLines]) => {
          const convertedLines = configLines.map(
            ({ openingMode, beginTime, preAuthorizationAmount }, index, array) => ({
              openingMode,
              beginTime: DateTime.fromFormat(beginTime, 'HH:mm').toString(),
              endTime:
                index < array.length - 1
                  ? DateTime.fromFormat(array[index + 1].beginTime, 'HH:mm').toString()
                  : DateTime.fromObject({ hour: 0, minute: 0 }).toString(),
              ...(preAuthorizationAmount !== undefined ? { preAuthorizationAmount } : {}),
            }),
          )
          return [Number(day), convertedLines]
        }),
      )

      setOpeningConfigs(convertedConfigs)
      setIsInitializedDoorConfig(true)
    }
  }, [doorBaseConfiguration, isInitializedDoorConfig])

  const handleDayConfigChange = useCallback(
    (dayNumber: number, configCards: OpeningConfigLine[]) => {
      setOpeningConfigs((prev) => ({
        ...prev,
        [dayNumber]: configCards,
      }))
    },
    [],
  )

  return (
    <>
      <Toast />
      <StoreDetailsLayout
        storeId={storeId}
        tab="hours"
        headerActions={
          <>
            <Button
              sx={{ marginRight: '8px' }}
              variant="outlined"
              onClick={() => setIsInitializedDoorConfig(false)}
            >
              Annuler
            </Button>

            <AuthorizedButton
              permission={['edit', 'stores.schedules.schedule']}
              sx={{ marginLeft: '8px' }}
              variant="contained"
              onClick={() => mutateUpdateConfigDoor()}
            >
              Enregistrer
            </AuthorizedButton>
          </>
        }
      >
        <Box sx={{ marginTop: '16px' }}>
          <OpeningHoursConfigPanel
            storeId={storeId}
            openingConfigs={openingConfigs}
            onDayConfigChange={handleDayConfigChange}
          />
        </Box>
      </StoreDetailsLayout>
    </>
  )
}
